import type { FormKey } from "@v1/contexts/ElementEditorErrorContext";
import type { ElementErrorType } from "@v1/utils/element";
import type { ReploElementType } from "schemas/generated/element";

import * as React from "react";

import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import Input from "@v1/components/common/designSystem/Input";
import { useElementEditorErrorContext } from "@v1/contexts/ElementEditorErrorContext";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import { MAX_SECTION_NAME_LENGTH } from "@v1/utils/element";
import { docs } from "@v2/utils/docs";
import { exhaustiveSwitch, isEmpty } from "replo-utils/lib/misc";

import { ElementEditorErrors } from "./ElementEditorErrors";

const ElementNameEditor: React.FC<{
  initialName?: string;
  type: ReploElementType;
  inputName: string;
  onChange: (newName: string) => void;
  isDisabled?: boolean;
}> = ({ initialName, onChange, inputName, type, isDisabled }) => {
  const { errorMapping, setErrors, clearErrors } =
    useElementEditorErrorContext();
  const elementTypeSingularName = exhaustiveSwitch({ type })({
    page: "Page",
    shopifyArticle: "Post",
    shopifyProductTemplate: "Template",
    shopifySection: "Section",
  });

  const mostRelevantError = getMostRelevantError({
    errorMapping,
    elementType: type,
  });

  const errorMappingIncludesPathAlreadyExists =
    errorMapping["path"]?.includes("pathAlreadyExists");
  const handleNameChange = React.useCallback(
    (name: string) => {
      // NOTE (Sebas, 2023-11-28): This is in charge of the input validation. In case the
      // user enters a name, we remove the error from the list of errors and also we remove
      // the "pathIsEmpty" error because the path is automatically generated from the name.
      if (name.length >= 1) {
        clearErrors("title", ["nameIsEmpty"]);
        clearErrors("path", ["pathIsEmpty"]);
      }
      if (name.length === 0) {
        setErrors("title", ["nameIsEmpty"]);
      }
      if (type === "shopifySection" && errorMappingIncludesPathAlreadyExists) {
        clearErrors("path", ["pathAlreadyExists"]);
      }
      // NOTE (Martin, 2023-10-02, USE-457): We need to validate name against
      // invisible Unicode characters since we disallow those in element's names.
      // https://stackoverflow.com/a/24231346
      // Note (Noah, 2024-03-18, USE-818): We need to have a pretty specific regex
      // here - we want to include printable characters, but exclude control characters,
      // format characters, invisible separators, etc
      //
      // From senior developer ChatGPT, this regular expression will keep:
      // \p{L}: any kind of letter from any language.
      // \p{M}: marks that are meant to be combined with another character (like diacritical marks).
      // \p{N}: any kind of numeric character in any script.
      // \p{P}: any kind of punctuation character.
      // \p{Z}: any kind of separator character.
      // -~: the range of printable ASCII characters, including the space.
      //
      // ALSO: we use a different slugify function for the page path, so diacritical marks
      // and such will never get included in the page path
      const newName = name.replace(/[^\p{L}\p{M}\p{N}\p{P}\p{Z} -~]+/gu, "");
      onChange(newName);
    },
    [
      clearErrors,
      errorMappingIncludesPathAlreadyExists,
      onChange,
      setErrors,
      type,
    ],
  );

  /* TODO (Yuxin, 2022-03-22) For some reason, the first input is highlighted when
      we open the modal, and onOpenAutoFocus={(e) => e.stopPropagation()} doesn't
      actually work https://github.com/radix-ui/primitives/discussions/935 */

  const inputProps = useOverridableInput({
    // Note (Evan, 2024-12-09): Initialize to the de-folderized version of the name, so we don't end up writing names
    // with slashes (and triggering folder-moving logic on the backend).
    value: initialName ?? "",
    onValueChange: handleNameChange,
    timeout: 0,
  });

  return (
    <DeprecatedLabeledControl
      label="Name"
      className="flex items-center"
      id="page-data-name"
      error={
        mostRelevantError ? (
          <ElementEditorErrors
            errorType={mostRelevantError}
            elementType={type}
          />
        ) : null
      }
      size="sm"
    >
      <Input
        {...inputProps}
        id="page-data-name"
        autoFocus
        placeholder={`${elementTypeSingularName} Name`}
        name={inputName}
        size="sm"
        maxLength={
          type === "shopifySection" ? MAX_SECTION_NAME_LENGTH : undefined
        }
        autoComplete="off"
        isDisabled={isDisabled ?? false}
      />

      {isDisabled && (
        <span className="text-muted pt-2 text-xs">
          This {elementTypeSingularName} cannot be renamed because it has been
          published.
          <div className="mt-1">
            <a
              href={docs.warnings.cannotRenameSection}
              target="_blank"
              className="underline"
              rel="noreferrer"
            >
              Learn More
            </a>
          </div>
        </span>
      )}

      {type === "shopifySection" &&
        initialName &&
        initialName.length >= MAX_SECTION_NAME_LENGTH && (
          <span className="text-muted pt-2 text-xs">
            Shopify limits section names to {MAX_SECTION_NAME_LENGTH}{" "}
            characters.
          </span>
        )}
    </DeprecatedLabeledControl>
  );
};

const getMostRelevantError = (opts: {
  errorMapping: Record<FormKey, ElementErrorType[] | null>;
  elementType: ReploElementType;
}): ElementErrorType | null => {
  const { errorMapping, elementType } = opts;
  if (!isEmpty(errorMapping)) {
    if (errorMapping["title"]?.includes("isInvalidSectionName")) {
      return "isInvalidSectionName";
    }

    if (errorMapping["title"]?.includes("nameIsEmpty")) {
      return "nameIsEmpty";
    }

    // NOTE (Chance 2024-03-25): No relevance ranking from here, just make sure
    // we show *some* error
    if (errorMapping["title"] && errorMapping["title"].length > 0) {
      return errorMapping["title"][0]!;
    }

    // Note (Noah, 2024-07-15): If it's a shopify section, we show path errors as
    // part of the section name, since we want to tell the user that the section
    // already exists. For sections, the path is always generated from the name
    if (
      elementType === "shopifySection" &&
      errorMapping["path"]?.includes("pathAlreadyExists")
    ) {
      return "pathAlreadyExists";
    }
  }

  return null;
};

export default ElementNameEditor;
